import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import { colord } from 'colord';
import { symbolToPictureMarkerMap } from './uscgSymbology';
import {
  createPolylineSymbol,
  createPolylineFromPoints,
} from './polylineGraphics';
import {
  BOOM_RESOURCES,
  isLocationProperties,
  isSpecialLocationProperties,
  OIL_SPILL_RESOURCE,
  type Location,
} from '../../../../../types/locations';
import type { InternalLayerVersionWithFeatures } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { normalizeOpacity } from './normalizeOpacity';

const DEFAULT_POLYGON_FILL = [30, 64, 175, 0.25];
const DEFAULT_POLYGON_OUTLINE = [30, 58, 138, 1];

/** Reusable Popup Template to avoid object recreation in loops */
const COMMON_POPUP_TEMPLATE = new PopupTemplate({
  title: '{name}',
  content: [
    {
      type: 'text',
      text: `
        <b>Incident:</b> {incidentName} <br/>`,
    },
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'name', label: 'Name' },
        { fieldName: 'incidentName', label: 'Incident' },
        { fieldName: 'resourceType', label: 'Resource Type' },
        { fieldName: 'description', label: 'Description' },
      ],
    },
  ],
});

/** Helper to transform a single feature into one or more Esri Graphics */
function createGraphicsForFeature(
  feature: Location,
  incidentName?: string
): Graphic[] {
  const { resourceType, properties } = feature;
  const graphics: Graphic[] = [];

  // 1. Resolve icon symbol
  const symbolKey = resourceType;
  const isBoom =
    symbolKey === BOOM_RESOURCES.PROPOSED ||
    symbolKey === BOOM_RESOURCES.COMPLETED;
  const isOilSpill = symbolKey === OIL_SPILL_RESOURCE;
  const iconSymbol = symbolToPictureMarkerMap[symbolKey];
  if (!iconSymbol && !isSpecialLocationProperties(properties)) {
    console.error('Feature location or symbol not supported', feature);
    return [];
  }

  // 2. Handle Multi-point Geometries (Booms & Oil Spills)
  if (isSpecialLocationProperties(properties)) {
    const points = [...properties.points].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    if (points.length === 0) return [];

    // Only create a line for booms when a line is present (line is optional)
    if (properties.line && isBoom) {
      graphics.push(
        new Graphic({
          geometry: createPolylineFromPoints(points),
          symbol: createPolylineSymbol('proposedBoom'),
          attributes: { ...feature, incidentName },
          popupTemplate: COMMON_POPUP_TEMPLATE,
        })
      );
    }

    // Create a line connecting all points in order for oil spills
    if (isOilSpill && points.length >= 2) {
      graphics.push(
        new Graphic({
          geometry: createPolylineFromPoints(points),
          symbol: createPolylineSymbol('oilSpill'),
          attributes: { ...feature, incidentName },
          popupTemplate: COMMON_POPUP_TEMPLATE,
        })
      );
    }

    // Booms also get a marker at every vertex/point
    if (isBoom && iconSymbol) {
      points.forEach((point) => {
        graphics.push(
          new Graphic({
            geometry: new Point({
              longitude: point.longitude,
              latitude: point.latitude,
            }),
            symbol: iconSymbol,
            attributes: {
              ...feature,
              pointOrder: point.order,
              incidentName,
            },
            popupTemplate: COMMON_POPUP_TEMPLATE,
          })
        );
      });
    }

    return graphics;
  }

  // 3. Handle Standard Single Point Geometries
  if (isLocationProperties(properties) && iconSymbol) {
    graphics.push(
      new Graphic({
        geometry: new Point({
          longitude: properties.longitude,
          latitude: properties.latitude,
        }),
        symbol: iconSymbol,
        attributes: { ...feature, incidentName },
        popupTemplate: COMMON_POPUP_TEMPLATE,
      })
    );
  }

  // 4. Handle GeoJSON geometry properties (custom polygon)
  if (resourceType === 'customPolygon') {
    const geometry = feature.geometry;

    if (geometry.type === 'Polygon') {
      const polygonColor =
        'polygonColor' in properties ? properties.polygonColor : undefined;
      const polygonOpacity =
        'polygonOpacity' in properties ? properties.polygonOpacity : undefined;

      const parsedColor = polygonColor ? colord(polygonColor) : null;
      const rgb = parsedColor?.isValid() ? parsedColor.toRgb() : null;
      const fillOpacity = normalizeOpacity(polygonOpacity);
      const fillColor = rgb
        ? [rgb.r, rgb.g, rgb.b, fillOpacity]
        : DEFAULT_POLYGON_FILL;
      const outlineColor = rgb
        ? [rgb.r, rgb.g, rgb.b, 1]
        : DEFAULT_POLYGON_OUTLINE;

      graphics.push(
        new Graphic({
          geometry: new Polygon({ rings: geometry.coordinates }),
          symbol: {
            type: 'simple-fill',
            color: fillColor,
            outline: {
              color: outlineColor,
              width: 2,
            },
          },
          attributes: feature,
          popupTemplate: COMMON_POPUP_TEMPLATE,
        })
      );
    }
  }

  return graphics;
}

interface SyncParams {
  map: __esri.Map;
  internalLayerVersionsMap: Map<string, InternalLayerVersionWithFeatures>;
  internalLayerVisibilityById: Map<string, boolean>;
  layersRef: React.MutableRefObject<Map<string, GraphicsLayer>>;
}

/** Syncs internal layer version features to GraphicsLayer(s) on the map. */
export function syncInternalLayers({
  map,
  internalLayerVersionsMap,
  internalLayerVisibilityById,
  layersRef,
}: SyncParams) {
  // Update or Create Layers
  for (const [versionId, entry] of internalLayerVersionsMap) {
    const layerId = entry.internalLayer.id;
    const isVisible = internalLayerVisibilityById.get(layerId) ?? true;
    const features = entry.featuresQuery.data ?? [];

    let layer = layersRef.current.get(versionId);

    if (!layer) {
      layer = new GraphicsLayer({
        id: `internal-version-${versionId}`,
        title: `Internal Layer: ${versionId}`,
      });
      map.add(layer);
      layersRef.current.set(versionId, layer);
    }

    // Update Visibility
    const visibilityChanged = layer.visible !== isVisible;
    layer.visible = isVisible;

    // rymi-TODO: still flickering, if only visibility changed and data count is identical, don't redraw
    if (
      visibilityChanged &&
      layer.graphics.length > 0 &&
      layer.graphics.length === features.length
    ) {
      continue;
    }

    // Refresh Graphics
    layer.removeAll();
    const allGraphics = features.flatMap((f) =>
      createGraphicsForFeature(f, entry.internalLayer.incident?.name)
    );
    layer.addMany(allGraphics);
  }

  // Cleanup: Remove layers that are no longer in the map
  const currentVersionIdSet = new Set(internalLayerVersionsMap.keys());
  for (const [id, layer] of layersRef.current.entries()) {
    if (!currentVersionIdSet.has(id)) {
      map.remove(layer);
      layersRef.current.delete(id);
    }
  }
}
